import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useMemo } from 'react';
import { compact } from 'lodash-es';
import { Controller, useFormContext } from 'react-hook-form';
import { MenuItem, Select, Tooltip, useStateInRef } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { billingTriggersTextsPath, makeInitialOneTimeInStages } from '~/business-logic/billingLogic';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useLocaleKeys } from '~/i18n';
import { serviceBillingModel } from '~/models';
import { RecurrencePeriodTypes } from '~/models/service-billing';
import { PreapprovalPeriodType } from '~/models/service-billing/model';
import { connectControllerToTextField } from '~/utils/react-hook-form.utils';
import { getUpdatedTriggerIfInvalid } from '../BillingTrigger/BillingTrigger.utils';
import { getOccurrenceDisabledData, serviceBuilderTextsPath } from '../ServiceBuilder.utils';
const getOccurrenceOptions = (showOnDemand)=>compact([
        showOnDemand ? {
            value: 'onDemand'
        } : null,
        {
            value: 'onetime'
        },
        {
            value: 'inStages'
        },
        {
            value: RecurrencePeriodTypes.Weekly
        },
        {
            value: RecurrencePeriodTypes.BiWeekly
        },
        {
            value: RecurrencePeriodTypes.Monthly
        },
        {
            value: RecurrencePeriodTypes.Quarterly
        },
        {
            value: RecurrencePeriodTypes.Yearly
        }
    ]);
export const deprecatedOccurrenceTextFieldTestIds = {
    OccurrenceTextFieldTestIds: {
        Select: 'sb-occurrence',
        Option: (value)=>"sb-occurrence-opt-".concat(value)
    }
};
export const GetOccurrenceTextFieldTID = (parentTestId)=>getTestIds({
        parentTestId,
        deprecatedTestIds: deprecatedOccurrenceTextFieldTestIds
    });
export default function OccurrenceTextField(props) {
    const { serviceOrigin, service } = props;
    const { OccurrenceTextFieldTestIds } = useMemo(()=>GetOccurrenceTextFieldTID(), []);
    const { t: sbTexts } = useLocaleKeys(serviceBuilderTextsPath);
    const { t: occurrenceTexts } = useLocaleKeys('billing.occurrence');
    const getProps = useStateInRef(props);
    const { control, watch, trigger, setValue, getValues } = useFormContext();
    const { disabled, tooltip } = getOccurrenceDisabledData({
        serviceOrigin,
        texts: sbTexts,
        service
    });
    const featureFlags = useFeatureFlags();
    const { YEARLY_AUTOMATIC_SERVICE, ON_DEMAND } = featureFlags;
    const billingTrigger = watch('trigger');
    const { t: billingTriggersTexts } = useLocaleKeys(billingTriggersTextsPath);
    const showOnDemand = !!ON_DEMAND;
    useEffect(()=>{
        trigger('recurrencePeriod');
    }, [
        billingTrigger,
        trigger
    ]);
    return /*#__PURE__*/ _jsx(Controller, {
        control: control,
        name: "recurrencePeriod",
        rules: {
            validate: (value)=>{
                if (billingTrigger === 'ongoing' && value === RecurrencePeriodTypes.Yearly && !YEARLY_AUTOMATIC_SERVICE) {
                    return occurrenceTexts.validation.invalidValue();
                }
                return true;
            }
        },
        render: (renderProps)=>{
            const _connectControllerToTextField = connectControllerToTextField(renderProps), { onChange } = _connectControllerToTextField, rest = _object_without_properties(_connectControllerToTextField, [
                "onChange"
            ]);
            return /*#__PURE__*/ _jsx(Tooltip, {
                title: tooltip,
                children: /*#__PURE__*/ _jsx(Select, _object_spread_props(_object_spread({
                    mobile: {
                        renderAsCard: true
                    },
                    bi: {
                        name: 'billing_occurrence',
                        data: {
                            previous_value: rest.value
                        }
                    },
                    collapseHelperText: false
                }, rest), {
                    label: occurrenceTexts.label(),
                    testId: OccurrenceTextFieldTestIds.Select(),
                    disabled: disabled,
                    onChange: (e)=>{
                        const newRecurrencePeriod = e.target.value;
                        onChange(e);
                        if (newRecurrencePeriod === 'inStages') {
                            const { service } = getProps();
                            const existingOneTimeInStages = service ? serviceBillingModel.oneTimeInStages(service.billing) : null;
                            setValue('oneTimeInStagesBilling', existingOneTimeInStages || makeInitialOneTimeInStages(), {
                                shouldDirty: true
                            });
                        } else {
                            setValue('oneTimeInStagesBilling', null, {
                                shouldDirty: true
                            });
                        }
                        if (newRecurrencePeriod === 'onDemand') {
                            const currentPeriod = getValues('preapprovalPeriod');
                            if (!currentPeriod || currentPeriod === PreapprovalPeriodType.None) {
                                setValue('preapprovalPeriod', PreapprovalPeriodType.Monthly, {
                                    shouldDirty: true
                                });
                            }
                        }
                        const previousRecurrencePeriod = rest.value;
                        if (previousRecurrencePeriod === 'onDemand' && newRecurrencePeriod !== 'onDemand') {
                            const { service } = getProps();
                            const updatedTrigger = getUpdatedTriggerIfInvalid({
                                t: billingTriggersTexts,
                                currentTrigger: getValues('trigger'),
                                pricing: getValues('pricing'),
                                recurrencePeriod: newRecurrencePeriod,
                                serviceOrigin,
                                isExistingService: !!service,
                                featureFlags
                            });
                            if (updatedTrigger) {
                                setValue('trigger', updatedTrigger, {
                                    shouldDirty: true
                                });
                            }
                        }
                    },
                    children: getOccurrenceOptions(showOnDemand).map((param)=>{
                        let { value } = param;
                        return /*#__PURE__*/ _jsx(MenuItem, {
                            value: value,
                            testId: OccurrenceTextFieldTestIds.Option(value),
                            children: serviceBillingModel.extendedRecurrencePeriodText(value, false)
                        }, value);
                    })
                }))
            });
        }
    });
}
